import React from 'react'
import {useIntl} from 'react-intl'
import {useQueryClient} from 'react-query'
import {toast} from 'react-toastify'
import {
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
} from '../core/QueryResponseProvider'
import {useListView} from '../core/ListViewProvider'
import {useQueryRequest} from '../core/QueryRequestProvider'
import {User} from '../core/_models'
import {ID, initialQueryState} from '../../../../../_dashboard/helpers'
import {Pagination} from '../../../../../_dashboard/helpers/crud-helper/pagination/PaginationComponent'
import {patchOne, deleteOne} from '../../../../services/common_requests'
import {useAuth} from '../../../../modules/auth/core/Auth'
import {canDeleteTeamUsers, canEditTeamUsers, canToggleTeamUserStatus} from '../../../../utils/roleUtils'
import {useConfirm} from '../../../../components/ConfirmProvider'

const getColorFromString = (str: string) => {
  const colors = [
    'primary',
    'success',
    'info',
    'warning',
    'danger',
    'dark',
    'light',
  ]
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

const getInitials = (first?: string, last?: string) => {
  const a = (first || '').slice(0, 1)
  const b = (last || '').slice(0, 1)
  return `${a}${b}`.toUpperCase() || '??'
}

interface UsersTableProps {
  setFilters: React.Dispatch<
    React.SetStateAction<{
      page: number
      size: number
      status: string
      role: string
    }>
  >
}

const UsersTable: React.FC<UsersTableProps> = ({setFilters}) => {
  const intl = useIntl()
  const confirm = useConfirm()

  const formatRole = (role: string) =>
    intl.formatMessage({
      id: `USERS.ROLE.${role}`,
      defaultMessage: role.replace(/^ROLE_/, '').replace(/_/g, ' '),
    })
  const queryClient = useQueryClient()
  const {data: rawData} = useQueryResponseData()
  const data = Array.isArray(rawData) ? rawData : []
  const pagination = useQueryResponsePagination()
  const isLoading = useQueryResponseLoading()
  const {setItemIdForUpdate, setViewUserId} = useListView()
  const {state: queryReqState, updateState: updateQueryReq} = useQueryRequest()
  const {auth} = useAuth()
  const currentRole = auth?.role as string | undefined
  const showEdit = canEditTeamUsers(currentRole)
  const showToggle = canToggleTeamUserStatus(currentRole)
  const showDelete = canDeleteTeamUsers(currentRole)

  const handleViewDetails = (id: ID) => {
    if (id) setViewUserId(id)
  }

  const onPageChange = (updates: Partial<{page: number; pageSize: number}>) => {
    setFilters((prev) => ({
      ...prev,
      page: updates.page ?? prev.page,
      size: updates.pageSize ?? prev.size,
    }))
  }

  const toggleStatus = async (user: User, active: boolean) => {
    try {
      const res = await patchOne({isActive: active}, `/users/${user._id}/status`)
      if (res?.payload?.code === 2021) {
        toast.success(
          intl.formatMessage({id: active ? 'USERS.TOAST.ACTIVATED' : 'USERS.TOAST.BLOCKED'}),
        )
        queryClient.invalidateQueries(['users'])
        queryClient.invalidateQueries(['clients'])
      } else {
        toast.error((res as any)?.payload?.message || intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}))
      }
    } catch (e) {
      toast.error(intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}))
    }
  }

  const removeUser = async (user: User) => {
    const ok = await confirm({
      message: intl.formatMessage({id: 'USERS.CONFIRM.DELETE', defaultMessage: 'Delete user?'}),
    })
    if (!ok) return
    try {
      const res = await deleteOne(user._id, '/users')
      if ((res as any)?.payload?.code === 2020) {
        toast.success(intl.formatMessage({id: 'USERS.TOAST.DELETED'}))
        queryClient.invalidateQueries(['users'])
        queryClient.invalidateQueries(['clients'])
      } else {
        toast.error(intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}))
      }
    } catch {
      toast.error(intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}))
    }
  }

  const sortId = queryReqState?.sort as string | undefined
  const sortOrder = queryReqState?.order as 'asc' | 'desc' | undefined

  const toggleSort = (id: string) => {
    if (id === 'avatar' || id === 'actions') return
    if (sortId !== id) {
      updateQueryReq({sort: id, order: 'asc', ...initialQueryState})
      return
    }
    if (sortOrder === 'asc') {
      updateQueryReq({sort: id, order: 'desc', ...initialQueryState})
    } else if (sortOrder === 'desc') {
      updateQueryReq({sort: undefined, order: undefined, ...initialQueryState})
    } else {
      updateQueryReq({sort: id, order: 'asc', ...initialQueryState})
    }
  }

  const headerClass = (id: string) =>
    sortId === id && sortOrder ? `cursor-pointer table-sort-${sortOrder}` : 'cursor-pointer'

  return (
    <div className='card-body py-3'>
      <div className='table-responsive mb-4'>
        <table className='table align-middle gs-0 gy-4'>
          <thead>
            <tr className='fw-bold text-muted bg-light'>
              <th className='ps-4 min-w-100px rounded-start' onClick={() => toggleSort('avatar')}>
                {intl.formatMessage({id: 'USERS.TABLE.AVATAR'})}
              </th>
              <th className={`min-w-150px ${headerClass('name')}`} onClick={() => toggleSort('name')}>
                {intl.formatMessage({id: 'USERS.TABLE.NAME'})}
              </th>
              <th className={`min-w-150px ${headerClass('email')}`} onClick={() => toggleSort('email')}>
                {intl.formatMessage({id: 'USERS.TABLE.EMAIL'})}
              </th>
              <th className={`min-w-150px ${headerClass('role')}`} onClick={() => toggleSort('role')}>
                {intl.formatMessage({id: 'USERS.TABLE.ROLE'})}
              </th>
              <th className={`min-w-100px ${headerClass('status')}`} onClick={() => toggleSort('status')}>
                {intl.formatMessage({id: 'USERS.TABLE.STATUS'})}
              </th>
              <th className={`min-w-150px ${headerClass('lastConnection')}`} onClick={() => toggleSort('lastConnection')}>
                {intl.formatMessage({id: 'USERS.TABLE.LAST_CONNECTION'})}
              </th>
              <th className='min-w-200px text-end rounded-end pe-4' onClick={() => toggleSort('actions')}>
                {intl.formatMessage({id: 'COMMON.ACTIONS'})}
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.map((user: User) => {
              const userColor = getColorFromString(user.firstName || '')
              const last = (user as any).lastName || ''
              return (
                <tr key={user._id}>
                  <td className='ps-4'>
                    <div
                      className='symbol symbol-35px me-5 cursor-pointer'
                      onClick={() => handleViewDetails(user._id)}
                    >
                      {user.avatar ? (
                        <img src={user.avatar} alt='' className='rounded-circle' />
                      ) : (
                        <div
                          className={`symbol-label bg-light-${userColor} text-${userColor} fw-bold`}
                        >
                          {getInitials(user.firstName, last)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className='text-gray-800 fw-bold text-hover-primary mb-1 fs-6 cursor-pointer'
                      onClick={() => handleViewDetails(user._id)}
                    >
                      {[user.firstName, last].filter(Boolean).join(' ')}
                    </span>
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold text-hover-primary mb-1 fs-6'>
                      {user.email}
                    </span>
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold text-hover-primary mb-1 fs-6'>
                      {formatRole(
                        typeof user.role === 'object' ? user.role.name : String(user.role),
                      )}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge fw-semibold ${
                        user.status?.toUpperCase() === 'ACTIVE' ? 'bg-success' : 'bg-danger'
                      }`}
                      style={{color: '#fff'}}
                    >
                      {intl.formatMessage({id: `USERS.STATUS.${user.status?.toUpperCase()}`})}
                    </span>
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold text-hover-primary mb-1 fs-6'>
                      {user.lastConnection ? new Date(user.lastConnection).toLocaleDateString() : ''}
                    </span>
                  </td>
                  <td className='text-end pe-4'>
                    {showEdit && (
                      <button
                        type='button'
                        className='btn btn-icon btn-light-primary btn-sm me-1'
                        title={intl.formatMessage({id: 'USERS.ACTION.EDIT'})}
                        onClick={() => {
                          setViewUserId(null)
                          setItemIdForUpdate(user._id)
                        }}
                      >
                        <i className='bi bi-pencil fs-5' />
                      </button>
                    )}
                    {showToggle && (
                      <button
                        type='button'
                        className='btn btn-icon btn-light-warning btn-sm me-1'
                        title={intl.formatMessage({id: 'USERS.ACTION.TOGGLE'})}
                        onClick={() =>
                          toggleStatus(user, user.status?.toUpperCase() !== 'ACTIVE')
                        }
                      >
                        <i className='bi bi-shield-lock fs-5' />
                      </button>
                    )}
                    {showDelete && (
                      <button
                        type='button'
                        className='btn btn-icon btn-light-danger btn-sm'
                        title={intl.formatMessage({id: 'USERS.ACTION.DELETE'})}
                        onClick={() => removeUser(user)}
                      >
                        <i className='bi bi-trash fs-5' />
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {pagination && (
        <Pagination
          pagination={pagination}
          isLoading={isLoading}
          updateState={onPageChange}
        />
      )}
    </div>
  )
}

export {UsersTable}
